<template>
    <card class="card-plain">
        <form>
          <div class="card-body row pt-0">
                <div class="col-12">
                    <div class="row">
                        <div class="col-md-6">
                            <h4 class="m-0 pb-2">{{$t('Create News')}}</h4>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <fg-input :label="$t('Title')"
                                      name="title"
                                      v-model="form.title"
                                      :required="validations.type.required"
                                      v-validate="validations.title"
                                      :error="errors.first('title')">
                            </fg-input>
                        </div>
                        <div class="col-md-3">
                            <fg-input :label="$t('Visibility')"
                                      name="type"
                                      :required="validations.type.required"
                                      :error="errors.first('type')">
                                <el-select size="large"
                                           class="select-primary"
                                           v-validate="validations.type"
                                           v-model="form.type">
                                    <el-option v-for="option in types"
                                               :value="option.value"
                                               :label="$t(option.label)"
                                               :key="option.value"
                                               :disabled="option.disabled">
                                    </el-option>
                                </el-select>
                            </fg-input>
                        </div>
                        <div class="col-md-3">
                            <fg-input v-if="form.type === 'team'" 
                              :label="$t('Team')" 
                              name="team" 
                              required>
                                <input-picker v-model="form.teams" data-type="newsTeam" multiple />
                            </fg-input>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-lg-12 col-md-12 col-sm-12 pb-4">
                            <upload v-model="form.image" :image-url="imageUrl" :change="fileChange"/>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12 form-group">
                            <fg-input :label="$t('Content')"
                                      :error="errors.first('content')"
                                      :required="validations.content.required">
                                <vue-editor v-model="form.content"
                                            data-vv-name="content"
                                            :editorToolbar="editorSettings.toolbar"
                                            :editorOptions="editorSettings.options"
                                            v-validate="validations.content"
                                            :error="errors.first('content')"
                                />
                            </fg-input>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <p-checkbox v-model="form.published">{{ $t('Published') }}</p-checkbox>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card-footer text-right actions">
                <p-button round native-type="submit" type="primary" class="btn btn-fill btn-wd btn-one-size-dropdown" :disabled="loading"
                          @click.prevent="save">
                    {{$t('Save')}}
                </p-button>
            </div>

        </form>
    </card>
</template>

<script>
  import { NewsRecord } from '@/store/models/News'
  import { Select, Option, Input } from 'element-ui'
  import InputPicker from '@/components/Dashboard/Components/InputPicker'
  import Upload from '@/components/Dashboard/Components/BasicUpload'
  import { VueEditor } from 'vue2-editor'
  import PButton from '../../../UIComponents/Button'

  export default {
    name: 'NewsEdit',
    components: {
      PButton,
      [Select.name]: Select,
      [Option.name]: Option,
      [Input.name]: Input,
      Upload,
      InputPicker,
      VueEditor
    },
    data () {
      return {
        loading: false,
        imageUrl: null,
        form: new NewsRecord(),
        types: [
            {
                value: 'global',
                label: 'global',
                disabled: !this.$auth.user.permissions.news_publisher
            },
            {
                value: 'team',
                label: 'team'
            }],
        validations: {
          title: {
            required: true
          },
          content: {
            required: true,
            min: 1
          },
          type: {
            required: true
          }
        },
        editorSettings: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['clean']
          ],
          options: {
            formats: [
              'bold',
              'italic',
              'underline',
              'list'
            ]
          }
        }
      }
    },
    computed: {
      newsId: function () {
        return this.$route.params.id
      },
      news: function () {
        return this.newsId ? NewsRecord.find(this.newsId) : null
      }
    },
    methods: {
      async init () {
        if (this.$route.name === 'NewsCreate') {
          this.form = new NewsRecord(NewsRecord.defaults())
        } else {
          await this.fetch()
        }
      },
      async fetch () {
        if (!this.newsId) {
          return
        }
        try {
          this.loading = true
          await NewsRecord.api().get(`/news/${this.newsId}`, { save: true })
        } catch (e) { console.log('e', e)} finally {
          this.loading = false
        }
      },
      async save () {
        const isValid = await this.$validator.validateAll()
        if (!isValid) {
          return
        }

        const { title, content, type, teams, published } = this.form
        const data = { title, content, type, published }
        if (type === 'team') {
          if (!teams) {
            this.$notify({ message: this.$t('Missing team selection'), type: 'danger' })
            return
          }
          data.teams = teams
        }

        if (this.imageUrl) {
          data.image = this.imageUrl
        } else {
          data.image = this.form.image
        }

        try {
          this.loading = true

          if (this.form._id) {
            const result = await NewsRecord.api().put(`/news/${this.newsId}`, data, { save: false })
            const image = result.response.data.image ? result.response.data.image : ''
            this.form = new NewsRecord(Object.assign({}, result.response.data, { image }))
            this.$notify({ message: this.$t('Successfully saved.'), type: 'success' })

          } else {
            const result = await NewsRecord.api().post(`/news`, data, { save: false })
            this.form = new NewsRecord(result.response.data)
            if (result.response.data._id) {
              this.$notify({ message: this.$t('Successfully saved.'), type: 'success' })
              this.$router.push({ name: 'NewsEdit', params: { id: result.response.data._id } })
            }
          }

          this.imageUrl = null

        } catch (e) {
          this.$notify({ message: e.message, type: 'danger' })

        } finally {
          this.loading = false
        }

      },
      fileChange (url) {
        this.imageUrl = url
      }
    },
    async mounted () {
      await this.init()
    },
    watch: {
      news (val) {
        if (val) {
          this.form = new NewsRecord(val)
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .button-distance {
    margin-right: 10px !important;
  }

  @media (max-width: 590px) {
      .btn-one-size-dropdown {
          width: 100%;
          padding-left: 0px;
          padding-right: 0px;
          padding-right: 10px !important;
      }

      .card-footer {
        padding: 0px;
      }

      .button-distance {
        margin-bottom: 10px !important;
      }
    }
</style>
